import { computed, defineComponent, ref, watch, } from '@vue/composition-api';
import { QuestionType, SURVEY_ANSWER_SEPARATOR, } from '@/api/v2/requests/surveys';
import { Btn, BtnTheme, BtnSize } from '@/components/Btn';
import SurveyQuestion from './SurveyQuestion.vue';
import { useForm } from '@/composables';
import { submitSurveyAnswers } from '@/api/v2/requests/surveys';
import cloneDeep from 'lodash.clonedeep';
import { useAuthUser } from '@/composables';
import Vue from 'vue';
import { useSurveyDataCache } from '@/composables/use-survey-data-cache';
import { useSurveyBranching } from '@/composables/use-survey-branching';
import SurveyModalShell from './SurveyModalShell.vue';
const FORM_ID = 'survey-modal-form';
export default defineComponent({
    name: 'SurveyModal',
    components: {
        SurveyQuestion,
        Btn,
        SurveyModalShell,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
        place: {
            type: Object,
            default: null,
        },
        totalCount: Number,
        show: Boolean,
        readonly: {
            type: Boolean,
            default: false,
        },
        onSuccess: {
            type: Function,
            default: null,
        },
    },
    setup(props, ctx) {
        const { authUserId } = useAuthUser();
        const { surveyCache, setRecord: setSurveyCache, deleteRecord: deleteSurveyCache, } = useSurveyDataCache(props.survey.id);
        const handleFileNameMapping = (mapping) => {
            if (!props.readonly) {
                setSurveyCache({
                    ...surveyCache.value,
                    fileNameMappings: {
                        ...surveyCache.value.fileNameMappings,
                        ...mapping,
                    },
                });
            }
        };
        const normalizeQuestions = (questions) => {
            return cloneDeep(questions).map((q) => {
                // Ensure `value` exists from the start (Vue 2 reactivity: adding new props later is not reactive).
                const question = {
                    ...q,
                    value: q.value ?? '',
                    // Ensure these keys exist from the start so computed validation reacts to changes.
                    attachmentFile: q.attachmentFile ?? '',
                    comment: q.comment ?? '',
                };
                if (question.type === QuestionType.CHECKBOX) {
                    const selected = new Set((question.value || '')
                        .split(SURVEY_ANSWER_SEPARATOR)
                        .filter(Boolean));
                    question.options = (question.options || []).map((opt) => ({
                        ...opt,
                        isSelected: opt.isSelected ?? selected.has(opt.label),
                    }));
                }
                return question;
            });
        };
        const referenceForm = ref(cloneDeep({
            id: props.survey.id,
            questions: normalizeQuestions(props.survey.questions),
        }));
        const { form, hasUnsavedChanges } = useForm(referenceForm);
        if (!props.readonly) {
            surveyCache.value.answers.forEach((answer, idx) => {
                const q = form.value.questions[idx];
                q.value = answer;
                q.attachmentFile = surveyCache.value.files[idx];
                q.comment = surveyCache.value.comments[idx];
                // Re-apply cached checkbox selections to `options[].isSelected`
                // (otherwise after refresh `value` is restored but UI remains unchecked).
                if (q.type === QuestionType.CHECKBOX && q.options) {
                    const selected = new Set((answer || '').split(SURVEY_ANSWER_SEPARATOR).filter(Boolean));
                    q.options.forEach((opt) => {
                        opt.isSelected = selected.has(opt.label);
                    });
                }
            });
        }
        const getAnswer = (question) => {
            if (question.type === QuestionType.CHECKBOX) {
                // SurveyQuestion writes selected options into `question.value` to make validation reactive.
                if (question.value) {
                    return question.value;
                }
                return question
                    .options.filter((option) => option.isSelected)
                    .map((option) => option.label)
                    .join(SURVEY_ANSWER_SEPARATOR);
            }
            else {
                return question.value;
            }
        };
        const { isQuestionVisible } = useSurveyBranching(computed(() => form.value.questions), (idx) => getAnswer(form.value.questions[idx]));
        const hasBranchingRules = (idx) => {
            const q = form.value.questions[idx];
            return (Array.isArray(q.branchingRules) &&
                q.branchingRules.length > 0);
        };
        const parseAnswers = () => {
            const files = [];
            form.value.questions.forEach((question, idx) => {
                files.push(isQuestionVisible(idx) ? question?.attachmentFile || '' : '');
            });
            const answers = form.value.questions.map((q, idx) => isQuestionVisible(idx) ? getAnswer(q) : '');
            const comments = form.value.questions.map((q, idx) => isQuestionVisible(idx) ? q.comment || '' : '');
            return {
                answers,
                comments,
                files,
                fileNameMappings: surveyCache.value.fileNameMappings,
            };
        };
        const hasTriedSubmit = ref(false);
        const isAggregateComplete = (q, answer) => {
            if (!answer)
                return false;
            try {
                const parsed = JSON.parse(answer);
                const items = q.items ?? [];
                return items.every((item) => typeof parsed[item.label] === 'string' &&
                    parsed[item.label].trim() !== '');
            }
            catch {
                return false;
            }
        };
        const invalidAnswerQuestions = computed(() => {
            if (!hasTriedSubmit.value || props.readonly)
                return {};
            const { answers } = parseAnswers();
            const invalid = {};
            form.value.questions.forEach((q, idx) => {
                if (!isQuestionVisible(idx))
                    return;
                if (!q.isRequired)
                    return;
                if (q.type === QuestionType.AGGREGATE) {
                    if (!isAggregateComplete(q, answers[idx])) {
                        invalid[idx] = true;
                    }
                }
                else if (!answers[idx]) {
                    invalid[idx] = true;
                }
            });
            return invalid;
        });
        const invalidFileQuestions = computed(() => {
            if (!hasTriedSubmit.value || props.readonly)
                return {};
            const { files } = parseAnswers();
            const invalid = {};
            form.value.questions.forEach((q, idx) => {
                if (!isQuestionVisible(idx))
                    return;
                const missingRequiredFile = Boolean(q.isQuestionWithFile) &&
                    Boolean(q.isQuestionFileRequired) &&
                    !files[idx];
                if (missingRequiredFile) {
                    invalid[idx] = true;
                }
            });
            return invalid;
        });
        const invalidQuestions = computed(() => {
            if (!hasTriedSubmit.value || props.readonly)
                return {};
            const invalid = {};
            form.value.questions.forEach((q, idx) => {
                if (invalidAnswerQuestions.value[idx] ||
                    invalidFileQuestions.value[idx]) {
                    invalid[idx] = true;
                }
            });
            return invalid;
        });
        const questionLoadingStates = ref({});
        const isLoading = computed(() => Object.values(questionLoadingStates.value).some(Boolean));
        const isSubmitting = ref(false);
        const persistTimer = ref(null);
        watch(form, () => {
            if (!props.readonly) {
                if (persistTimer.value) {
                    window.clearTimeout(persistTimer.value);
                }
                persistTimer.value = window.setTimeout(() => {
                    setSurveyCache(parseAnswers());
                }, 350);
            }
        }, { deep: true });
        const updateShow = () => {
            ctx.emit('close');
        };
        const handleSubmit = async (e) => {
            if (props.readonly) {
                return;
            }
            e.preventDefault();
            const { answers, comments, files } = parseAnswers();
            const areRequiredFieldsFilled = !answers.some((answer, idx) => {
                const q = form.value.questions[idx];
                if (!isQuestionVisible(idx) || !q.isRequired)
                    return false;
                if (q.type === QuestionType.AGGREGATE) {
                    return !isAggregateComplete(q, answer);
                }
                return !answer;
            });
            const areRequredFilesUploaded = !files.some((file, idx) => isQuestionVisible(idx) &&
                form.value.questions[idx].isQuestionWithFile &&
                form.value.questions[idx].isQuestionFileRequired &&
                !file);
            if (!areRequiredFieldsFilled || !areRequredFilesUploaded) {
                hasTriedSubmit.value = true;
                ctx.root.$toast.error(ctx.root.$t('surveys.survey-required-fields-error'));
                // Scroll to the first invalid question for better UX.
                ctx.root.$nextTick(() => {
                    const firstInvalidIdx = Object.keys(invalidQuestions.value)
                        .map(Number)
                        .sort((a, b) => a - b)[0];
                    if (Number.isFinite(firstInvalidIdx)) {
                        const el = document.getElementById(`survey-question-${firstInvalidIdx}`);
                        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
                return;
            }
            isSubmitting.value = true;
            try {
                await submitSurveyAnswers(form.value.id, answers, files, comments, authUserId.value, props.place?.id || '');
                deleteSurveyCache();
                hasTriedSubmit.value = false;
                ctx.root.$toast.success(ctx.root.$t('surveys.success'));
                if (ctx.listeners.save) {
                    ctx.emit('save', form.value.id);
                }
                if (props.onSuccess) {
                    props.onSuccess();
                }
            }
            catch {
                ctx.root.$toast.error(ctx.root.$t('surveys.error'));
            }
            finally {
                isSubmitting.value = false;
            }
        };
        return {
            FORM_ID,
            form,
            hasUnsavedChanges,
            BtnTheme,
            BtnSize,
            getAnswer,
            updateShow,
            surveyCache,
            handleSubmit,
            isLoading,
            isSubmitting,
            invalidQuestions,
            invalidAnswerQuestions,
            invalidFileQuestions,
            questionLoadingStates,
            handleFileNameMapping,
            isQuestionVisible,
            hasBranchingRules,
            handleChangeQuestionLoading: ({ id, value, }) => {
                Vue.set(questionLoadingStates.value, id.value, value);
            },
        };
    },
});
